<template>
  <div
    class="w-full h-full px-4 tablet-h:px-5 pt-[14px] pb-[22px] bg-gray-200/20 rounded-xl"
  >
    <div class="flex items-center gap-x-2.5 mb-2">
      <TextIcon class="size-[22px] text-gray-800" />

      <div class="font-medium text-[15px] leading-[20px] text-gray-800">
        Transcript
      </div>
    </div>

    <div class="w-full h-px mb-4 bg-gray-200"></div>

    <MatchMarkers :markers="match.markers" :side="side" />

    <div class="relative">
      <div class="text-[12px] text-gray-500 leading-[20px] mb-1.5">
        Transcript
      </div>

      <div ref="container" class="mb-[14px] relative">
        <svg
          class="absolute inset-0 z-0 pointer-events-none"
          style="overflow: visible"
          aria-hidden="true"
        >
          <path
            v-for="(d, groupId) in groupPaths"
            :key="groupId"
            :d="d"
            class="stroke-2"
            :class="pathClasses"
          />
        </svg>

        <div
          v-for="data in formattedTranscript"
          :key="data.timestamp"
          class="relative z-10 flex text-[14px] leading-[20px]"
        >
          <div class="min-w-[90px] text-gray-500 tabular-nums">
            {{ data.timestamp }}
          </div>
          <div class="relative">
            <MatchTag
              v-if="data.isGroupStart"
              :id="number"
              :level="match.score ? match.level : undefined"
              class="absolute -top-3 -left-2 z-20"
            />
            <div
              :class="{ 'whitespace-pre-wrap': data.highlight }"
              :data-line-group="data.highlight ? data.groupId : undefined"
              v-html="sanitizeHtml(data.text)"
            ></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { graphql } from '@/shared/graphql/router'

export const TranscriptMatchFragment = graphql(`
  fragment TranscriptMatchFields on TranscriptMatch {
    id
    location
    summary
    score
    level
    start
    end
    markers {
      hash
      color
      shine
      visible
    }
    transcript {
      timestamp
      text
      highlight
    }
  }
`)
</script>

<script setup lang="ts">
import {
  type FragmentType,
  useFragment,
} from '@/shared/graphql/router/fragment-masking'
import MatchMarkers from './MatchMarkers.vue'
import MatchTag from './matches/MatchTag.vue'
import { ScoreLevel, type Transcript } from '@/shared/graphql/router/graphql'
import { sanitizeHtml } from '@/shared/utils/sanitize'
import {
  buildHighlightPath,
  measureLineRects,
  type LineRect,
} from '../utils/coords'
import { TextIcon } from '@dacr/icons'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

const props = defineProps<{
  match: FragmentType<typeof TranscriptMatchFragment>
  url?: string | null
  number: number
  side?: 'registered' | 'infringing'
  allowPlayback?: boolean
}>()

const match = useFragment(TranscriptMatchFragment, props.match)

type FormattedTranscriptMatch = Transcript & {
  groupId?: string
  isGroupStart: boolean
}

const container = ref<HTMLElement>()
const groupPaths = ref<Record<string, string>>({})

const formattedTranscript = computed(() => {
  let currentGroupId: string | null = null
  let groupCounter = 0

  return match.transcript.slice().reduce((acc, data) => {
    if (!data) return acc

    let groupId: string | undefined
    let isGroupStart = false

    if (data.highlight) {
      if (!currentGroupId) {
        currentGroupId = `g${groupCounter++}`
        isGroupStart = true
      }
      groupId = currentGroupId
    } else {
      currentGroupId = null
    }

    acc.push({ ...data, groupId, isGroupStart })
    return acc
  }, [] as FormattedTranscriptMatch[])
})

const pathClasses = computed(() => {
  if (match.summary) {
    if (match.level === ScoreLevel.High) return 'fill-[#f5dbda] stroke-red-500'
    if (match.level === ScoreLevel.Medium)
      return 'fill-[#f5ebdc] stroke-yellow-500'
    if (match.level === ScoreLevel.Low) return 'fill-[#dceff3] stroke-green-500'
  }
  return 'fill-[#e2e3e5] stroke-gray-500'
})

const updatePaths = () => {
  const root = container.value
  if (!root) return

  const byGroup: Record<string, LineRect[]> = {}
  for (const el of Array.from(
    root.querySelectorAll<HTMLElement>('[data-line-group]')
  )) {
    const groupId = el.dataset.lineGroup
    if (!groupId) continue
    byGroup[groupId] = byGroup[groupId] ?? []
    byGroup[groupId].push(...measureLineRects(el, root))
  }

  const paths: Record<string, string> = {}
  for (const groupId in byGroup) {
    paths[groupId] = buildHighlightPath(byGroup[groupId], 4, 2, 1)
  }
  groupPaths.value = paths
}

watch(formattedTranscript, async () => {
  await nextTick()
  updatePaths()
})

onMounted(async () => {
  await nextTick()
  updatePaths()
  document.fonts?.ready.then(updatePaths)
  window.addEventListener('resize', updatePaths)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updatePaths)
})
</script>
